import { Button as Btn } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslation } from "contexts/TranslationContext";
import BackButton from "shared/components/BackButton/BackButton";
import { useGetUserAppsQuery, useGetVendorsQuery } from "services/auth";
import routes, { appRoute } from "shared/constants/routes";
import env from "shared/constants/env";

import emptyProducts from "assets/svg/emptyProducts.svg";
import vendorPlaceholder from "assets/svg/vendorPlaceholder.png";

// ── Backend filter (Education / EDUCATIONFEES) ───────────────────────────────
// Backend supports `?subproductName=&productCode=`. Schools are uniquely
// identified by SUBPRODUCT_NAME="EDUCATIONFEES" + PRODUCT_CODE matching the
// EDU Temenos product. Same combo used in the create payload.
const SUBPRODUCT_NAME = "EDUCATIONFEES";
const PRODUCT_CODE = "SMB.TA.PERSONAL.FINANCE.EDU";
const PAGE_LIMIT = 20;

const SchoolListingV2 = () => {
  const navigate = useNavigate();
  const { translate, language } = useTranslation();
  const isArabic = language?.code === "ar";

  // Vendor ids whose logo failed to load. Tracked in state (rather than
  // mutating img.src in onError) so the fallback survives re-renders and is
  // applied at most once per vendor — otherwise a broken image retriggers
  // onError on every render and floods the image endpoint.
  const [failedLogos, setFailedLogos] = useState({});
  const markLogoFailed = (id) =>
    setFailedLogos((prev) => (prev[id] ? prev : { ...prev, [id]: true }));

  const { data: userApps } = useGetUserAppsQuery(
    JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID
  );

  const { data: vendorResponse, isLoading, isError } = useGetVendorsQuery({
    subproductName: SUBPRODUCT_NAME,
    productCode: PRODUCT_CODE,
    limit: PAGE_LIMIT,
  });

  // Server already filtered to EDUCATIONFEES; client narrowing is a defensive
  // no-op in case any stray vendor slips through (e.g. backend regression).
  const vendors = useMemo(
    () =>
      (vendorResponse?.data || []).filter(
        (v) => v?.SUBPRODUCT_NAME === SUBPRODUCT_NAME
      ),
    [vendorResponse]
  );

  // Localized field accessors. Falls back to the other language if a side is empty.
  const vendorName = (v) =>
    (isArabic ? v?.VENDOR_NAME_AR : v?.VENDOR_NAME_EN) ||
    v?.VENDOR_NAME_EN ||
    v?.VENDOR_NAME_AR ||
    "-";
  const vendorDesc = (v) =>
    (isArabic ? v?.SHORT_DESC_AR : v?.SHORT_DESC_EN) ||
    v?.SHORT_DESC_EN ||
    v?.SHORT_DESC_AR ||
    "";

  const handleApplyNow = async (vendor) => {
    // Match the legacy 50-app cap behavior from SchoolListing.
    if (userApps?.length >= 50) {
      toast.error(translate("APPLICATION_ALREADY_EXISTS"));
      return;
    }

    try {
      const preAppStatus = await fetch(
        `${env.API_ENDPOINT_V2}app/pre-app-status?id=${
          JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID
        }`
      );
      const response = await preAppStatus.json();

      if (
        response?.result?.completedSteps.length >= 4 &&
        response?.result?.status === "COMPLETED"
      ) {
        // Route to V2 product page by SUBPRODUCT_NAME. Server-filtered to
        // EDUCATIONFEES so this is effectively always the schools page —
        // keeping the switch for parity with FeaturedProductsV2's routing.
        if (vendor?.SUBPRODUCT_NAME === "EDUCATIONFEES") {
          navigate(appRoute(routes.SCHOOLS_V2 + `/${vendor.ID}`));
        } else if (vendor?.SUBPRODUCT_NAME === "HEALTHCARE") {
          navigate(appRoute(routes.DENTAL_V2 + `/${vendor.ID}`));
        } else {
          toast.info(translate("VOUCHERS.COMING_SOON") || "Coming soon");
        }
      } else {
        navigate(appRoute(routes.PRE_APP));
        toast.error("Please complete pre-application steps and try again.");
      }
    } catch (error) {
      console.error("Pre-App Status error:", error);
      toast.error(
        "Something went wrong in checking pre app status. Please try again."
      );
    }
  };

  // Skeleton tile — mirrors the real card's footprint so the layout doesn't jump on load.
  const renderSkeletonCard = (idx) => (
    <div
      key={`skeleton-${idx}`}
      className="bg-white rounded-lg shadow-md animate-pulse"
    >
      <div className="relative w-full h-48 rounded-t-lg overflow-hidden bg-gray-200" />
      <div className="p-6">
        <div className="my-3">
          <div className="h-6 w-2/3 bg-gray-200 rounded mb-3" />
          <div className="h-4 w-full bg-gray-200 rounded mb-2" />
          <div className="h-4 w-1/2 bg-gray-200 rounded" />
        </div>
        <div className="h-10 w-32 bg-gray-200 rounded-full" />
      </div>
    </div>
  );

  return (
    <div className="rounded-xl mb-4 px-4 py-4 bg-white shadow-lg">
      <div className="p-5">
        {/* Back button — same layout as legacy SchoolListing */}
        <div className="flex items-center space-x-4">
          <div className="ml-3">
            <BackButton onClick={() => navigate(-1)} />
          </div>
          <span className="text-[#1F225B] text-2xl font-light">
            {translate("HOME.SCHOOL_PRODUCTS_TITLE")}
          </span>
        </div>

        <div className="container mx-auto px-4 my-5 pt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => renderSkeletonCard(i))
              : vendors.map((vendor, index) => (
                  <div
                    key={vendor.ID}
                    className="bg-white rounded-lg shadow-md"
                  >
                    <div className="relative w-full h-48 rounded-t-lg overflow-hidden">
                      <div className="relative w-full h-48 rounded-t-lg overflow-hidden bg-[linear-gradient(to_left,#E1DBDB,#F9F9F9)] flex items-center justify-center">
                        <img
                          src={
                            // OCI PAR signed URLs expire after ~1h — fall back once.
                            !vendor.LOGO_URL || failedLogos[vendor.ID]
                              ? vendorPlaceholder
                              : vendor.LOGO_URL
                          }
                          alt={`Vendor-Logo-${index}`}
                          className="relative z-10 p-4 w-[50%] max-h-full object-contain"
                          onError={() => markLogoFailed(vendor.ID)}
                        />
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="my-3">
                        <h3 className="text-[#1F225B] text-xl font-semibold mb-2">
                          {vendorName(vendor)}
                        </h3>
                        <p className="text-[#8E9196] text-base mb-4">
                          {vendorDesc(vendor)}
                        </p>
                      </div>

                      <Btn
                        className={
                          "rounded-full bg-primary text-white font-semibold p-4 w-fit border border-primary hover:!border-primary hover:border-solid hover:!text-primary"
                        }
                        onClick={() => handleApplyNow(vendor)}
                      >
                        {translate("HOME.CREATE_NEW")}
                      </Btn>
                    </div>
                  </div>
                ))}
          </div>

          {/* Empty / error states — match the rest of the app's tone */}
          {!isLoading && !isError && vendors.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <img src={emptyProducts} alt="No vendors" className="w-40 md:w-56" />
              <p className="text-[#5F5F5F] text-sm md:text-base text-center">
                {translate("HOME.NO_FEATURED_PRODUCTS") ||
                  "Currently, there are no products under this category"}
              </p>
            </div>
          )}
          {!isLoading && isError && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <LoadingOutlined style={{ fontSize: 32, color: "#8E9196" }} />
              <p className="text-[#5F5F5F] text-sm md:text-base text-center">
                {translate("SIGN_IN.SOMETHING_WENT_WRONG") ||
                  "Something went wrong. Please try again."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolListingV2;
